<template>
  <TableComp
    v-if="services"
    :name="$t('global.shared-hosting')"
    :headers="headers"
    :status="services.status"
    :pagination="services.pagination"
    :sort="services.sort"
    :number-of-items="services.list.length"
    empty-image="shared-hosting"
    v-on:update:list="updateList"
  >
    <template #desktop>
      <tr v-for="item in services.list" v-bind:key="item.id">
        <td>
          <div v-if="item.isStable" class="d-flex align-items-center">
            <router-link :to="getRouterLink(item)">{{ getDomain(item) }}</router-link>
          </div>
          <span v-else>{{ getDomain(item) }}</span>
        </td>
        <td class="text-center">{{ getType(item) }}</td>
        <td class="text-center">{{ getBandwidth(item) }}</td>
        <td class="text-center">{{ getAutoRenew(item) }}</td>
        <td class="text-center">{{ getPrice(item) }}</td>
        <td class="text-center">
          <div>{{ getRenewalDate(item) }}</div>
          <div v-if="item.cancellation" class="text-danger">
            <div>
              {{ $t("global.cancellation-pending") }}
            </div>
            <div>{{ getCancellationDate(item) }}</div>
          </div>
        </td>
        <td class="text-center text-capitalize">
          {{ getStatus(item) }}
        </td>
        <td class="text-end actions">
          <div v-if="item.isStable" class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(item)" />
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <loading-state :size="20" />
            </div>
            <div class="text-capitalize">{{ item.state }}...</div>
          </div>
        </td>
      </tr>
    </template>
    <template #mobile>
      <TableMobileRow v-for="item in services.list" v-bind:key="item.id">
        <template #header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              <div v-if="item.isStable" class="d-flex align-items-center">
                <router-link :to="getRouterLink(item)">
                  {{ getDomain(item) }}
                </router-link>
              </div>
              <span v-else>{{ getDomain(item) }}</span>
            </div>
            <div>
              <div v-if="item.isStable" class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(item)" />
              </div>
              <div v-else class="d-flex justify-content-center align-items-center">
                <div class="mr-2">
                  <loading-state :size="20" />
                </div>
                <div class="text-capitalize">{{ item.state }}...</div>
              </div>
            </div>
          </div>
        </template>
        <template #content>
          <div class="row">
            <div class="col">{{ $t("global.type") }}:</div>
            <div class="col">{{ getType(item) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.bandwidth") }}:</div>
            <div class="col">{{ getBandwidth(item) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.auto-renew") }}:</div>
            <div class="col">{{ getAutoRenew(item) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.price") }}:</div>
            <div class="col">{{ getPrice(item) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.renewal-date") }}:</div>
            <div class="col">
              <div>{{ getRenewalDate(item) }}</div>
              <div v-if="item.cancellation" class="text-danger">
                <div>
                  {{ $t("global.cancellation-pending") }}
                </div>
                <div>{{ getCancellationDate(item) }}</div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col text-capitalize">{{ getStatus(item) }}</div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import { dateFormat } from "leatherman-js/utils/formatters/time";
import LoadingState from "@/components/display/LoadingState";
import { moneyFormat } from "leatherman-js/utils/formatters/money";
import NavLink from "@/classes/NavLink";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";

export default {
  name: "SharedHostingTable",
  props: {
    services: Object,
  },
  components: {
    LoadingState,
    TableComp,
    TableMobileRow,
    ThreeDotsMenu,
  },
  setup(props, { root }) {
    const headers = [
      new TableHeader(root.$t("global.domain"), undefined),
      new TableHeader(root.$t("global.type"), "justify-content-center"),
      new TableHeader(root.$t("global.bandwidth"), "justify-content-center"),
      new TableHeader(root.$t("global.auto-renew"), "justify-content-center"),
      new TableHeader(root.$t("global.price"), "justify-content-center"),
      new TableHeader(root.$t("global.renewal-date"), "justify-content-center"),
      new TableHeader(root.$t("global.status"), "justify-content-center"),
      new TableHeader(),
    ];
    const menuItems = (item) => {
      const links = [
        new NavLink(root.$t("global.dashboard"), null, getRouterLink(item), false, "default"),
      ];

      if (item.isCancellable) {
        links.push(
          new NavLink(
            root.$t("global.request-cancellation"),
            null,
            () => {
              root.$router.push({
                name: "request-cancellation",
                params: { id: item.id, type: "sharedHosts" },
              });
            },
            false,
            "danger"
          )
        );
      }

      return links;
    };

    const getAutoRenew = (item) => root.$t(`global.${item.autoRenew ? "yes" : "no"}`);
    const getBandwidth = (item) =>
      root.$t("global.shared-hosting-bandwidth", {
        allotted: item.bandwidth.allotted,
        units: item.bandwidth.units,
        used: item.bandwidth.used,
      });
    const getDomain = (item) => item.domain;
    const getPrice = (item) =>
      `${moneyFormat(item.billing.amount)} / ${item.billing.abbreviatedFrequency}`;
    const getRenewalDate = (item) => item.billing.formattedNextBillDate;
    const getCancellationDate = (item) => dateFormat(item.cancellation.scheduledDate);
    const getRouterLink = (item) => ({ name: "shared-host", params: { sharedHostId: item.id } });
    const getStatus = (item) => (item.isStable ? item.status : item.state);
    const getType = (item) => item.description;

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("sharedHosts/getList", {
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    return {
      headers,
      menuItems,
      getAutoRenew,
      getBandwidth,
      getDomain,
      getPrice,
      getRenewalDate,
      getCancellationDate,
      getRouterLink,
      getStatus,
      getType,
      updateList,
    };
  },
};
</script>
